"use client";

import { useState } from "react";
import DatePicker from "react-datepicker";
import { sl } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import ModalDropBox from "@/app/components/appcomponents/ModalDropBox";

/**
 * The dates of the deceased, entered with the same boxes and pickers as the obituary form (AddObituary): Dan, Mesec,
 * Leto, each opening the same inline picker. Without the "Samo leto" tick: if only the year is picked, only the year
 * is saved (as the obituary does with birthYear / deathYear); a full date is saved as a date.
 *
 *   value: { birth: { day, month, year }, death: { day, month, year } }   (numbers or null)
 */
export const emptyDates = { birth: { day: null, month: null, year: null }, death: { day: null, month: null, year: null } };

const pad = (n) => String(n).padStart(2, "0");
const isFull = (p) => Boolean(p.day && p.month && p.year);
const isEmpty = (p) => !p.day && !p.month && !p.year;
// 31. 2. is not a day: the date is real only if it survives a round trip.
const isRealDate = (p) => {
  const d = new Date(p.year, p.month - 1, p.day);
  return d.getFullYear() === p.year && d.getMonth() === p.month - 1 && d.getDate() === p.day;
};

/** What is wrong with the dates, in words for the visitor, or null when they can be sent. */
export function datesError({ birth, death }) {
  if (!death.year) return "Vpiši datum slovesa ali vsaj leto";
  for (const [label, p] of [["slovesa", death], ["rojstva", birth]]) {
    if (isEmpty(p) || (p.year && !p.month && !p.day)) continue;
    if (!isFull(p)) return `Datum ${label}: vpiši celoten datum ali samo leto`;
    if (!isRealDate(p)) return `Datum ${label} ne obstaja`;
  }
  if (birth.year) {
    if (birth.year > death.year) return "Datum rojstva mora biti pred datumom slovesa";
    if (isFull(birth) && isFull(death) && new Date(birth.year, birth.month - 1, birth.day) > new Date(death.year, death.month - 1, death.day)) {
      return "Datum rojstva mora biti pred datumom slovesa";
    }
  }
  return null;
}

// The fields sent to the server, as the obituary form sends them: a full date OR only a year.
export function datesPayload({ birth, death }) {
  const part = (p, dateKey, yearKey) => (isFull(p) ? { [dateKey]: `${p.year}-${pad(p.month)}-${pad(p.day)}` } : p.year ? { [yearKey]: p.year } : {});
  return { ...part(birth, "birthDate", "birthYear"), ...part(death, "deathDate", "deathYear") };
}

// "23.05.1956 - 12.04.2025" or "1930 - 1999" (a year alone stays a year).
export function datesText({ birth, death }) {
  const show = (p) => (isFull(p) ? `${pad(p.day)}.${pad(p.month)}.${p.year}` : p.year ? String(p.year) : "");
  return [show(birth), show(death)].filter(Boolean).join(" - ");
}

function DateField({ label, value, onChange, lastDate, openAt, open, toggle }) {
  const id = (part) => `${label}-${part}`;
  const base = value.year ? new Date(value.year, (value.month || 1) - 1, 1) : openAt;
  const picked = isFull(value) ? new Date(value.year, value.month - 1, value.day) : null;
  const set = (patch) => onChange({ ...value, ...patch });
  const popover = (part, props) =>
    open === id(part) && (
      <div className="absolute left-0 z-20 mt-12 rounded border bg-white shadow-lg">
        <DatePicker inline locale={sl} maxDate={lastDate} openToDate={base} {...props} />
      </div>
    );
  return (
    <div className="mt-5 first:mt-0">
      <span className="text-[14px] leading-[24px] text-[#6D778E]">{label}</span>
      <div className="relative mt-1 flex flex-row gap-x-3">
        <ModalDropBox widthClass="min-w-0 flex-1" placeholder="dan" onClick={() => toggle(id("day"))} isSelectText={value.day || ""} />
        {popover("day", {
          selected: picked,
          dateFormat: "d",
          // As on the obituary form: picking a day in the calendar sets the whole date.
          onChange: (date) => {
            set({ day: date.getDate(), month: date.getMonth() + 1, year: date.getFullYear() });
            toggle(null);
          },
        })}
        <ModalDropBox widthClass="min-w-0 flex-1" placeholder="mesec" onClick={() => toggle(id("month"))} isSelectText={value.month || ""} />
        {popover("month", {
          selected: picked,
          dateFormat: "MM",
          showMonthYearPicker: true,
          onChange: (date) => {
            set({ month: date.getMonth() + 1 });
            toggle(null);
          },
        })}
        <ModalDropBox widthClass="min-w-0 flex-1" placeholder="leto" onClick={() => toggle(id("year"))} isSelectText={value.year || ""} />
        {popover("year", {
          selected: picked,
          dateFormat: "yyyy",
          showYearPicker: true,
          yearItemNumber: 10,
          onChange: (date) => {
            set({ year: date.getFullYear() });
            toggle(null);
          },
        })}
      </div>
      {(value.day || value.month) && (
        <button type="button" onClick={() => set({ day: null, month: null })} className="mt-1 text-[12px] text-[#0A85C2] underline">
          samo leto (pobriši dan in mesec)
        </button>
      )}
    </div>
  );
}

export default function DeceasedDatesFields({ value, onChange, lastDate }) {
  const [open, setOpen] = useState(null);
  const toggle = (id) => setOpen((current) => (current === id || id === null ? null : id));
  return (
    <div>
      <DateField label="Datum rojstva" value={value.birth} onChange={(birth) => onChange({ ...value, birth })} lastDate={lastDate} openAt={new Date(1951, 0, 1)} open={open} toggle={toggle} />
      <DateField label="Datum slovesa" value={value.death} onChange={(death) => onChange({ ...value, death })} lastDate={lastDate} openAt={lastDate} open={open} toggle={toggle} />
    </div>
  );
}
